import { AttachmentsUploadResponse } from '../types/Upload/AttachmentResponse'
import {
	BuildSheet,
	ScaffoldRequestForm,
	ScaffoldUpdateRecord,
} from '../types/Scaffold/ScaffoldRequest'
import { getAll, getOne, patch, post, postWithResponse } from './CommonServices/httpService'
import { uploadImages } from './CommonServices/imageAttachmentService'
import { ScaffoldEstimation, ScaffoldFilters, ScaffoldWorkItem } from '../types/Scaffold/Scaffold'
import { WorkBreakdowns } from '../types/Project/WorkBreakdowns'
import { SmallWorker } from '../types/Employee/Employee'
import { CreateBuildSheetData } from '../types/Scaffold/ScaffoldRequest'
import { Coordinates } from '../types/Core/Coordinates'
import { FormSubmissionRequest } from '../types/Form/FormSubmission'

export const createScaffoldRequest = async (
	projectId: number,
	formDefinitionId: number,
	submittedById: number | null,
	formData: FormSubmissionRequest,
	files?: File[],
) => {
	const attachmentFileNames = files?.map((file) => file.name)

	const body: FormSubmissionRequest = {
		...formData,
		specialFields: {
			...(formData.specialFields ?? {}),
			...(attachmentFileNames ? { attachmentFileNames } : {}),
		},
	}
	const response = await postWithResponse<{
		insertedRequest: ScaffoldRequestForm
		tokens: AttachmentsUploadResponse[]
	}>(
		`/ScaffoldRequests/${projectId}?formDefinitionId=${formDefinitionId}${
			submittedById ? `&submittedById=${submittedById}` : ''
		}`,
		body,
	)
	if (files && response.data) {
		await uploadImages(response.data.tokens, files)
	}
	return response.data.insertedRequest
}

export const getScaffoldOrders = async (filters: ScaffoldFilters, assigneeId?: number) => {
	const queryParams = new URLSearchParams()
	if (filters.page) queryParams.append('page', filters.page.toString())
	if (filters.status) queryParams.append('status', filters.status)
	if (filters.tagNumber) queryParams.append('tagNumber', filters.tagNumber.toString())
	if (filters.requestedOn) queryParams.append('requestedOn', filters.requestedOn.toString())
	if (filters.requiredBefore)
		queryParams.append('requiredBefore', filters.requiredBefore.toString())
	if (filters.action) queryParams.append('action', filters.action)
	if (filters.priority) queryParams.append('priority', filters.priority)
	if (filters.scaffoldRequestId)
		queryParams.append('scaffoldRequestId', filters.scaffoldRequestId.toString())
	if (assigneeId) queryParams.append('assigneeId', assigneeId.toString())
	const response = await getAll<{ scaffoldRequests: ScaffoldRequestForm[]; totalCount: number }>(
		`/ScaffoldRequests?${queryParams.toString()}`,
	)
	return response.data
}

export const searchTagNumber = async (tagNumber: string, projectId: number) => {
	const response = await getAll<number[]>(
		`/ScaffoldRequests/TagNumbers?searchText=${tagNumber}&projectId=${projectId}`,
	)
	return response.data
}

export const getProjectWBS = async () => {
	const response = await getAll<WorkBreakdowns[]>(`/ScaffoldRequests/WBS`)
	return response.data
}

export const searchAssignees = async (searchTerm: string) => {
	const response = await getAll<SmallWorker[]>(
		`/ScaffoldRequests/Assignees?searchText=${searchTerm}`,
	)
	return response.data
}

export const assignScaffoldRequestToWorker = async (
	scaffoldRequestId: number,
	employeeId: number,
) => {
	const response = await patch(`/ScaffoldRequests/${scaffoldRequestId}/Worker/${employeeId}`, {})
	return response.success
}

export const createScaffoldWorkItem = async (
	workItems: ScaffoldWorkItem[],
	metadataValues: Record<number, string>,
	scaffoldRequestId: number,
) => {
	const response = await post(`/ScaffoldRequests/${scaffoldRequestId}/WorkItems`, {
		workItems,
		metadataValues,
	})
	return response.success
}

export const createManualWorkItem = async (
	workItems: ScaffoldWorkItem[],
	metadataValues: Record<number, string>,
) => {
	const response = await post(`/ScaffoldRequests/WorkItems`, {
		workItems,
		metadataValues,
	})
	return response.success
}

export const getWorkItemParents = async (workItemId: number) => {
	const response = await getAll<ScaffoldWorkItem[]>(`/ScaffoldRequests/WorkItems/${workItemId}`)
	return response.data
}

export const getScaffoldDetailsByTask = async (taskId: number) => {
	const response = await getOne<ScaffoldRequestForm | null>(`/ScaffoldRequests/Task/${taskId}`)
	return response.data
}

export const createBuildSheet = async (buildSheetData: CreateBuildSheetData, taskId: number) => {
	const response = await post(`/ScaffoldRequests/Task/${taskId}/BuildSheet`, buildSheetData)
	return response.success
}

export const getBuildSheet = async (taskId: number) => {
	const response = await getOne<BuildSheet | null>(`/ScaffoldRequests/Task/${taskId}/BuildSheet`)
	return response.data
}

export const getTaskLocation = async (taskId: number) => {
	const response = await getOne<Coordinates | null>(`/ScaffoldRequests/Task/${taskId}/Coordinates`)
	return response.data
}
export const updateTaskLocation = async (taskId: number, coordinates: Coordinates) => {
	const response = await post(`/ScaffoldRequests/Task/${taskId}/Coordinates`, coordinates)
	return response.success
}

export const updateScaffoldRequestDecision = async (
	scaffoldRequestId: number,
	decision: 'Approved' | 'Declined' | 'OnHold',
	comment: string,
) => {
	const response = await patch(`/ScaffoldRequests/${scaffoldRequestId}/Approval`, {
		decision: decision === 'Approved' ? 1 : decision === 'Declined' ? 2 : 0,
		decisionComment: comment || null,
	})
	return response.success
}

export const getScaffoldUpdateRecord = async (tagNumber: number) => {
	const response = await getAll<ScaffoldUpdateRecord[]>(
		`/ScaffoldRequests/${tagNumber}/UpdateRecords`,
	)
	return response.data
}
export const createScaffoldUpdateRecord = async (
	scaffoldRequestId: number,
	note: string | null,
	status: 0 | 1 | 2,
	fallProtectionRequired: boolean,
	incompleteItems: string[],
) => {
	const response = await post(`/ScaffoldRequests/${scaffoldRequestId}/UpdateRecord`, {
		note,
		status,
		fallProtectionRequired,
		incompleteItems,
	})
	return response.success
}

export const getWorkerPendingEstimationsCount = async (assigneeId: number) => {
	const response = await getOne<number>(`/ScaffoldRequests/Worker/${assigneeId}/PendingEstimations`)
	return response.data
}
